import {
  InputText,
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  InputCurrency,
} from "@sellar-io/components";
import { OrderRulesGetResponseType } from "@sellar-io/api-v2/types";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";
import { v4 } from "uuid";
import { Icon, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { ORDER_RULE, ORDER_RULES } from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { OrderRulesEdit } from "./OrderRulesEdit";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { PriceTierFormValues } from "../../priceTiers/priceTier/PriceTierForm";
import { useOrderRules } from "../../../hooks/api/v2/orderRules/useOrderRules";
import { useOrderRulesUpdate } from "../../../hooks/api/v2/orderRules/useOrderRulesUpdate";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../../components/dropdown/Dropdown";
import { useEffect, useState } from "react";
import { OrderRulesDeleteDialog } from "./components/OrderRulesDeleteDialog";
import { AlertCompact } from "../../../components/alert/AlertCompact";

export const OrderRule = () => {
  const navigate = useNavigate();
  const orderRulesId = useParamAsNumber(ORDER_RULE, "orderRulesId");
  const { data, isPending, isError } = useOrderRules(orderRulesId);

  if (isError) {
    return <Navigate to={ORDER_RULES} />;
  }

  return (
    <>
      <NavigationBar
        leftIcon={"CHEVRON_LEFT"}
        title={`Edit Order Rules`}
        onClickLeftIcon={() => navigate(ORDER_RULE.href({ orderRulesId }))}
      />
      {isPending || !data ? (
        <SpinnerLegacy />
      ) : (
        <div className="mx-auto flex max-w-[900px] flex-col gap-3 bg-gray-100 p-2 lg:py-10">
          <OrderRuleFormEdit orderRules={data} />
          <OrderRulesEdit
            orderRulesId={orderRulesId}
            rules={data.PriceTierOrderRules}
          />
        </div>
      )}
    </>
  );
};

const formId = v4();

const OrderRuleFormEdit = ({
  orderRules,
}: {
  orderRules: OrderRulesGetResponseType;
}) => {
  const form = useForm<PriceTierFormValues>({
    defaultValues: {
      name: orderRules.name,
      minimumOrderPrice: formatPriceApiToInput(orderRules.minimumOrderPrice),
    },
  });

  const register = form.register;

  const [deleteDialogActive, setDeleteDialogActive] = useState(false);

  const orderRulesUpdateMutation = useOrderRulesUpdate(orderRules.id);

  const onSave = (data: PriceTierFormValues) => {
    const apiData = {
      name: data.name,
      minimumOrderPrice: formatPriceInputToApi(data.minimumOrderPrice) ?? 0,
    };
    orderRulesUpdateMutation.mutate(apiData);
  };

  const noRules =
    (!orderRules.minimumOrderPrice || orderRules.minimumOrderPrice === 0) &&
    orderRules.PriceTierOrderRules.length === 0;

  useEffect(() => {
    if (orderRules) {
      form.setValue(
        "minimumOrderPrice",
        formatPriceApiToInput(orderRules.minimumOrderPrice),
      );
      form.setValue("name", orderRules.name);
    }
  }, [form, orderRules]);

  return (
    <>
      {noRules && (
        <AlertCompact
          type="information"
          onClick={() => setDeleteDialogActive(true)}
        >
          <div className="flex w-full justify-between">
            <span>
              This order rules set contains no rules,{" "}
              <span className="font-semibold">do you want to delete it?</span>
            </span>
            <span className="flex items-center gap-2 font-semibold text-indigo-700">
              Delete <Icon icon="DELETE" />
            </span>
          </div>
        </AlertCompact>
      )}
      <Card className="bg-white">
        <CardHeader className="flex items-center justify-between gap-2 bg-white p-4">
          <div>
            <H5>Order rules properties</H5>
            <span className="text-zinc-500">
              Review and/ or edit this order rule set's properties
            </span>
          </div>

          <Dropdown>
            <DropdownButton>
              <span className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-zinc-100">
                <Icon icon={"DOTS_VERTICAL"} className="flex-none" />
              </span>
            </DropdownButton>
            <DropdownItems>
              <DropdownItem
                onClick={() => setDeleteDialogActive(true)}
                className="text-red-500"
              >
                Delete
              </DropdownItem>
            </DropdownItems>
          </Dropdown>
        </CardHeader>
        <CardContent className={"flex flex-col gap-2 p-4"}>
          <form
            className={"flex flex-col gap-2"}
            id={formId}
            onSubmit={form.handleSubmit(onSave)}
          >
            <div className="flex flex-col gap-1">
              <InputLabel>Name</InputLabel>
              <InputContainer>
                <InputText {...register("name", { required: true })} />
              </InputContainer>
            </div>
            <div className="flex flex-col gap-1">
              <InputLabel>Minimum order value</InputLabel>
              <InputContainer>
                <InputCurrency
                  showPrefix
                  allowNegative
                  {...register("minimumOrderPrice", { required: false })}
                />
              </InputContainer>
              <span className="text-zinc-500">
                You can leave this blank if you just want to include container
                rules
              </span>
            </div>
          </form>
          {orderRulesUpdateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(orderRulesUpdateMutation.error)?.response?.data
                  .message ?? "Unknown error occurred"
              }
            ></Alert>
          ) : null}
          <Button
            loading={orderRulesUpdateMutation.isPending}
            disabled={!form.formState.isDirty || !form.formState.isValid}
            form={formId}
            type={"submit"}
          >
            Save
          </Button>
        </CardContent>
      </Card>
      <OrderRulesDeleteDialog
        orderRulesId={orderRules.id}
        orderRulesName={orderRules.name}
        active={deleteDialogActive}
        setActive={setDeleteDialogActive}
      />
    </>
  );
};
